import React from 'react';
import { useController, Control } from 'react-hook-form';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';

type Option = {
  value: string;
  label: string;
  prependItem?: React.ReactNode;
};

type SelectInputProps = {
  name: string;
  control: Control<any>;
  options: Option[];
  placeholder: string;
  buttonClass?: string;
  optionsClass?: string;
  rules?: Record<string, unknown>;
};

export const SelectInput: React.FC<SelectInputProps> = ({
  name,
  control,
  options,
  placeholder,
  buttonClass,
  optionsClass,
  rules,
}) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control, rules });

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div>
      <Listbox onChange={onChange} value={value}>
        {({ open }) => (
          <div className="relative">
            <Listbox.Button
              className={twMerge(
                'flex w-full items-center justify-between rounded-xs border border-neutral-50 bg-white p-3 outline-none focus:border-neutral-100',
                buttonClass,
              )}
            >
              <div className="flex items-center">
                {selectedOption?.prependItem && <>{selectedOption.prependItem}</>}
                {selectedOption?.label || placeholder}
              </div>
              <ChevronDownIcon
                className={`h-6 w-auto transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              />
            </Listbox.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className={twMerge(
                  'absolute z-10 mt-1 max-h-50 w-full overflow-y-auto overflow-x-hidden rounded-xs border border-neutral-100 bg-white',
                  optionsClass,
                )}
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    className=" cursor-pointer gap-2 px-4 py-2 duration-100 ease-in hover:bg-neutral-10 ui-selected:bg-neutral-5 ui-selected:font-bold ui-active:bg-neutral-10"
                    value={option.value}
                    data-id={option.value}
                  >
                    <div className="flex items-center ">
                      {option.prependItem && <>{option.prependItem}</>}
                      {option.label}
                    </div>
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
      {error && <p className="text-sm text-red">{error.message}</p>}
    </div>
  );
};
